import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Lock, Eye, Database, Smartphone, Globe, 
  CheckCircle2, FileText, AlertCircle, Mail, MapPin, 
  Camera, Cpu, UserCheck, RefreshCw, KeyRound, Server,
  ChevronRight, ExternalLink, Search, Printer, Share2,
  Check, ShieldCheck, ArrowUpRight, Fingerprint, Bell, 
  HardDrive, HelpCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { useTheme } from '../../components/ThemeContext';

const PrivacyPolicyPage = () => {
  const { isDarkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [copiedLink, setCopiedLink] = useState(false);

  const kpis = [
    {
      icon: ShieldCheck,
      value: "AES-256",
      label: "Encryption at Rest",
      desc: "Cloud database & hardware device encryption",
      color: isDarkMode ? "#38BDF8" : "#1C4D88",
      bg: isDarkMode ? "bg-sky-950/50 border border-sky-800/40" : "bg-blue-50"
    },
    {
      icon: MapPin,
      value: "Zero Tracking",
      label: "Geofenced GPS Only",
      desc: "Shift attendance proof, no background telemetry",
      color: isDarkMode ? "#34D399" : "#059669",
      bg: isDarkMode ? "bg-emerald-950/50 border border-emerald-800/40" : "bg-emerald-50"
    },
    {
      icon: Lock,
      value: "0% Selling",
      label: "Zero Data Monetization",
      desc: "No third-party brokers, no advertising tracking",
      color: isDarkMode ? "#FBBF24" : "#D97706",
      bg: isDarkMode ? "bg-amber-950/50 border border-amber-800/40" : "bg-amber-50"
    },
    {
      icon: RefreshCw,
      value: "30 Days",
      label: "Erasure SLA",
      desc: "Guaranteed account & data deletion window",
      color: isDarkMode ? "#F87171" : "#DC2626",
      bg: isDarkMode ? "bg-rose-950/50 border border-rose-800/40" : "bg-red-50"
    }
  ];

  const sections = [
    {
      id: 'overview',
      category: 'general',
      title: '1. Overview & Enterprise Scope',
      icon: Globe,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            This Privacy Policy governs the collection, storage, and protection of data by <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>AxonMetric Global Private Limited</strong> ("AxonMetric", "we", "us", or "our") across the AxonMetric web portal (<a href="https://axonmetric.com" className={`font-bold hover:underline ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`}>https://axonmetric.com</a>) and the <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>AxonMetric Industrial O&M & Telemetry Mobile Application</strong> for iOS and Android.
          </p>
          <p>
            AxonMetric operates as a mission-critical renewable energy asset management, Operations & Maintenance (O&M), and real-time SCADA telemetry platform. Our systems are used by solar developers, EPC contractors, asset managers, and on-site certified field engineers. We maintain zero tolerance for commercial data monetisation and strictly adhere to global industrial cybersecurity principles.
          </p>
          <div className={`p-4 rounded-xl flex items-center gap-3 text-xs border ${
            isDarkMode 
              ? 'bg-sky-950/30 border-sky-800/40 text-sky-200' 
              : 'bg-blue-50 border-blue-100 text-[#1C4D88]'
          }`}>
            <Shield className={`w-5 h-5 shrink-0 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} />
            <span>
              <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Store Compliance Guarantee:</strong> Certified compliant with <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Apple App Store Review Guideline 5.1.1</strong> and <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Google Play Store Developer Policies</strong> for enterprise telemetry applications.
            </span>
          </div>
        </div>
      )
    },
    {
      id: 'collection',
      category: 'data',
      title: '2. Information We Collect',
      icon: Database,
      content: (
        <div className={`space-y-5 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            We collect only the information strictly necessary to provide industrial telemetry, technician shift management, predictive diagnostics, and enterprise authentication:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-5 rounded-2xl border transition-all ${
              isDarkMode 
                ? 'bg-slate-800/50 border-slate-700/80 hover:border-sky-500/40' 
                : 'bg-white border-gray-200 shadow-sm hover:border-[#1C4D88]/40'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  isDarkMode ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20' : 'bg-blue-100 text-[#1C4D88]'
                }`}>
                  <UserCheck className="w-5 h-5" />
                </div>
                <h4 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Personal & Account Data</h4>
              </div>
              <ul className={`text-xs space-y-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>Full Name, Official Designation & Department</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>Corporate Email Address & Contact Mobile Number</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>Tenant Company & Assigned Solar/Wind Plant Sites</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>Encrypted Password Hashes (Salted and never in plaintext)</span>
                </li>
              </ul>
            </div>

            <div className={`p-5 rounded-2xl border transition-all ${
              isDarkMode 
                ? 'bg-slate-800/50 border-slate-700/80 hover:border-teal-500/40' 
                : 'bg-white border-gray-200 shadow-sm hover:border-[#1C4D88]/40'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  isDarkMode ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20' : 'bg-teal-100 text-teal-800'
                }`}>
                  <Cpu className="w-5 h-5" />
                </div>
                <h4 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Industrial Telemetry & SCADA</h4>
              </div>
              <ul className={`text-xs space-y-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-teal-400 shrink-0 mt-0.5" />
                  <span>Inverter performance, daily generation logs & PR metrics</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-teal-400 shrink-0 mt-0.5" />
                  <span>Weather Monitoring Station (WMS) solar radiation readings</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-teal-400 shrink-0 mt-0.5" />
                  <span>Breakdown tickets, equipment rectification logs & spares</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-teal-400 shrink-0 mt-0.5" />
                  <span>Permit To Work (PTW) high-voltage isolation records</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'mobile-permissions',
      category: 'mobile',
      title: '3. Mobile App & Sensor Permissions',
      icon: Smartphone,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            In compliance with store privacy guidelines, the AxonMetric mobile app requests device capabilities strictly when needed for core industrial features:
          </p>

          <div className="grid grid-cols-1 gap-3.5">
            <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700/80 shadow-md' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-start gap-3.5">
                <div className={`p-2.5 rounded-xl shrink-0 ${
                  isDarkMode ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/40' : 'bg-emerald-100 text-emerald-800'
                }`}>
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h5 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Precise Geolocation (GPS)</h5>
                    <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-emerald-950 text-emerald-300 border border-emerald-800/40' : 'bg-emerald-100 text-emerald-800'
                    }`}>Only In-Use</span>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Used <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>exclusively for verified on-site attendance marking</strong> and geo-tagged inspection proofs at solar plant substations. <em>Zero continuous background tracking when app is inactive.</em>
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700/80 shadow-md' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-start gap-3.5">
                <div className={`p-2.5 rounded-xl shrink-0 ${
                  isDarkMode ? 'bg-sky-950/60 text-sky-400 border border-sky-800/40' : 'bg-blue-100 text-[#1C4D88]'
                }`}>
                  <Camera className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h5 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Camera & Photo Library</h5>
                    <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-sky-950 text-sky-300 border border-sky-800/40' : 'bg-blue-100 text-[#1C4D88]'
                    }`}>User Triggered</span>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Used to capture "Before & After" photos for physical O&M checklists, record hardware damage photos, and scan equipment QR codes/barcodes for inventory.
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700/80 shadow-md' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-start gap-3.5">
                <div className={`p-2.5 rounded-xl shrink-0 ${
                  isDarkMode ? 'bg-purple-950/60 text-purple-400 border border-purple-800/40' : 'bg-purple-100 text-purple-800'
                }`}>
                  <Fingerprint className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h5 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Local Biometrics (Face ID / Fingerprint)</h5>
                    <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-purple-950 text-purple-300 border border-purple-800/40' : 'bg-purple-100 text-purple-800'
                    }`}>On-Device Only</span>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Enables rapid and secure unlock via native OS hardware enclave (Apple Touch ID/Face ID, Android BiometricPrompt). <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Biometric data never leaves your device.</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700/80 shadow-md' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-start gap-3.5">
                <div className={`p-2.5 rounded-xl shrink-0 ${
                  isDarkMode ? 'bg-amber-950/60 text-amber-400 border border-amber-800/40' : 'bg-amber-100 text-amber-800'
                }`}>
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h5 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Push Notifications</h5>
                    <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-amber-950 text-amber-300 border border-amber-800/40' : 'bg-amber-100 text-amber-800'
                    }`}>User Configurable</span>
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Delivers instant critical inverter breakdowns, fire safety alarms, and safety PTW approvals via Apple APNs and Google FCM.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'storing-strategy',
      category: 'security',
      title: '4. Data Storing Strategy & Multi-Tier Security',
      icon: Server,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            AxonMetric implements an enterprise defense-in-depth architecture covering mobile, transit, and cloud databases:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-5 rounded-2xl border space-y-2.5 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700/80' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDarkMode ? 'bg-sky-500/10 text-sky-400' : 'bg-blue-100 text-[#1C4D88]'
              }`}>
                <HardDrive className="w-4 h-4" />
              </div>
              <h5 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>1. Local Device Tier</h5>
              <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Auth tokens stored in OS-encrypted <code className={isDarkMode ? 'text-sky-300' : ''}>Expo SecureStore</code> (Keychain/Keystore). Offline checklist queues cached in isolated <code className={isDarkMode ? 'text-sky-300' : ''}>MMKV</code> memory for offline field work.
              </p>
            </div>

            <div className={`p-5 rounded-2xl border space-y-2.5 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700/80' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDarkMode ? 'bg-sky-500/10 text-sky-400' : 'bg-blue-100 text-[#1C4D88]'
              }`}>
                <Lock className="w-4 h-4" />
              </div>
              <h5 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>2. In-Transit Tier</h5>
              <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Enforced <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>TLS 1.3 / HTTPS</strong> with strict SSL Certificate Pinning and rolling 15-minute JWT tokens to prevent Man-In-The-Middle (MITM) interception.
              </p>
            </div>

            <div className={`p-5 rounded-2xl border space-y-2.5 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700/80' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDarkMode ? 'bg-sky-500/10 text-sky-400' : 'bg-blue-100 text-[#1C4D88]'
              }`}>
                <Database className="w-4 h-4" />
              </div>
              <h5 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>3. Server Storage Tier</h5>
              <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Enterprise databases protected with <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>AES-256 encryption at rest</strong>. Strict multi-tenant isolation ensures company data is segregated and inaccessible across organizations.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'subprocessors',
      category: 'security',
      title: '5. Sub-Processors & Zero Selling Policy',
      icon: ShieldCheck,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <div className={`p-4 rounded-xl border flex items-center gap-3 text-xs font-semibold ${
            isDarkMode 
              ? 'bg-emerald-950/30 border-emerald-800/50 text-emerald-300' 
              : 'bg-emerald-50 border-emerald-200 text-emerald-900'
          }`}>
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <span>Strict Guarantee: AxonMetric DOES NOT sell, rent, monetize, or trade any personal, operational, or plant telemetry data to third parties or advertising networks.</span>
          </div>

          <div className={`overflow-x-auto border rounded-2xl shadow-sm ${
            isDarkMode ? 'border-slate-800 bg-[#0F172A]' : 'border-gray-200 bg-white'
          }`}>
            <table className="min-w-full divide-y divide-gray-200 text-xs text-left">
              <thead className={`uppercase font-bold tracking-wider ${
                isDarkMode ? 'bg-slate-800/80 text-slate-300' : 'bg-gray-50 text-gray-700'
              }`}>
                <tr>
                  <th className="px-4 py-3">Partner Entity</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Data Handled</th>
                  <th className="px-4 py-3">Security Standard</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-slate-800 text-slate-300' : 'divide-gray-100 text-gray-600'}`}>
                <tr>
                  <td className={`px-4 py-3.5 font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Sentry.io</td>
                  <td className="px-4 py-3.5">Crash & Performance Diagnostics</td>
                  <td className="px-4 py-3.5">Device model, OS, anonymized stack traces</td>
                  <td className="px-4 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${
                      isDarkMode ? 'bg-sky-950/60 text-sky-300 border border-sky-800/40' : 'bg-blue-50 text-blue-700'
                    }`}>SOC-2 Type II</span>
                  </td>
                </tr>
                <tr>
                  <td className={`px-4 py-3.5 font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Apple APNs & Google FCM</td>
                  <td className="px-4 py-3.5">Alert Push Delivery</td>
                  <td className="px-4 py-3.5">Device push token, alert summary</td>
                  <td className="px-4 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${
                      isDarkMode ? 'bg-sky-950/60 text-sky-300 border border-sky-800/40' : 'bg-blue-50 text-blue-700'
                    }`}>ISO 27001</span>
                  </td>
                </tr>
                <tr>
                  <td className={`px-4 py-3.5 font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Cloud Infrastructure</td>
                  <td className="px-4 py-3.5">Server & Database Hosting</td>
                  <td className="px-4 py-3.5">Encrypted backups & telemetry database</td>
                  <td className="px-4 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${
                      isDarkMode ? 'bg-sky-950/60 text-sky-300 border border-sky-800/40' : 'bg-blue-50 text-blue-700'
                    }`}>Tier-4 Encrypted</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 'retention-deletion',
      category: 'rights',
      title: '6. Data Retention & Account Deletion Protocol',
      icon: RefreshCw,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            Users have the absolute right to request deletion of their account credentials and personal profile information at any time.
          </p>

          <div className={`p-5 rounded-2xl border space-y-3 ${
            isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-50 border-slate-200'
          }`}>
            <h5 className={`font-bold text-sm flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <Lock className={`w-4 h-4 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} /> Verified Deletion Process
            </h5>
            <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              When a deletion request is initiated via our web form or mobile app, personal data is irreversibly wiped within <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>30 calendar days</strong>. This includes usernames, passwords, device biometric bindings, contact info, and active tokens.
            </p>
            <div className="pt-2 flex flex-wrap gap-3">
              <Link
                to="/account-deletion"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-colors shadow-sm"
              >
                Go to Account Deletion Request Form <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          <div className={`p-4 rounded-xl border text-xs ${
            isDarkMode ? 'bg-amber-950/30 border-amber-800/50 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}>
            <strong className={isDarkMode ? 'text-amber-100' : 'text-amber-950'}>Regulatory Industrial Compliance Note:</strong> Certain electrical Permit To Work (PTW) logs previously signed by certified engineers for high-voltage grid isolation must be preserved for statutory safety audits as mandated by national electricity authorities. Personal records in these logs are pseudonymized where legally permissible.
          </div>
        </div>
      )
    },
    {
      id: 'contact',
      category: 'rights',
      title: '7. Grievance Officer & Contact Details',
      icon: Mail,
      content: (
        <div className={`space-y-4 text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          <p>
            In accordance with the <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> and applicable global data protection laws, inquiries or grievances may be addressed directly to our Grievance Officer:
          </p>

          <div className={`p-6 rounded-2xl border space-y-3 text-xs ${
            isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <div className={`font-extrabold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Grievance Redressal & Data Protection Officer</div>
            <div className={`font-bold ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>AxonMetric Global Private Limited</div>
            <div className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              <Mail className={`w-4 h-4 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} />
              <span>Direct Email: <a href="mailto:privacy@axonmetric.com" className={`font-bold underline ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`}>privacy@axonmetric.com</a> / <a href="mailto:info@axonmetric.com" className={`font-bold underline ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`}>info@axonmetric.com</a></span>
            </div>
            <div className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              <MapPin className={`w-4 h-4 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} />
              <span>Location: New Delhi, India</span>
            </div>
            <div className={`pt-2 border-t ${isDarkMode ? 'border-slate-700/60 text-slate-400' : 'border-gray-100 text-gray-500'}`}>
              Official Response Commitment: Acknowledgment within 48 business hours; complete resolution within 30 days.
            </div>
          </div>
        </div>
      )
    }
  ];

  const filteredSections = useMemo(() => {
    return sections.filter(sec => {
      const matchesCategory = activeTab === 'all' || sec.category === activeTab;
      const matchesSearch = !searchQuery || 
        sec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sec.id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [sections, activeTab, searchQuery]);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <Layout header={6} footer={6}>
      <div className={`legal-page min-h-screen font-sans transition-colors duration-200 ${
        isDarkMode ? 'bg-[#0B1120] text-slate-100' : 'bg-[#FAFCFF] text-gray-900'
      }`}>
        {/* Clean Website-Matched Hero Section */}
        <section className={`relative pt-28 pb-16 lg:pt-32 lg:pb-20 overflow-hidden border-b transition-colors duration-200 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#0B1120] via-[#0F172A] to-[#0B1120] border-slate-800' 
            : 'bg-gradient-to-br from-white via-gray-50 to-white border-gray-200/70'
        }`}>
          {/* Subtle decorative radial background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className={`absolute -top-24 -right-24 w-96 h-96 rounded-full transition-opacity duration-300 ${isDarkMode ? 'opacity-20' : 'opacity-10'}`} 
              style={{ background: isDarkMode ? 'radial-gradient(circle, #38BDF8 0%, transparent 70%)' : 'radial-gradient(circle, #1C4D88 0%, transparent 70%)' }}
            />
            <div 
              className={`absolute -bottom-32 -left-32 w-96 h-96 rounded-full transition-opacity duration-300 ${isDarkMode ? 'opacity-20' : 'opacity-10'}`} 
              style={{ background: isDarkMode ? 'radial-gradient(circle, #0284C7 0%, transparent 70%)' : 'radial-gradient(circle, #1C4D88 0%, transparent 70%)' }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              {/* Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 transition-colors shadow-sm ${
                isDarkMode 
                  ? 'border-slate-700 bg-slate-800/80 text-sky-400' 
                  : 'border-gray-200 bg-white text-gray-900'
              }`}>
                <Shield className="w-4 h-4" style={{ color: isDarkMode ? '#38BDF8' : '#1C4D88' }} />
                <span className={`text-xs sm:text-sm font-extrabold ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                  Google Play & Apple App Store Certified
                </span>
              </div>

              {/* Title */}
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Privacy Policy &{' '}
                <span className={isDarkMode ? "bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent" : "bg-gradient-to-r from-blue-600 to-[#1C4D88] bg-clip-text text-transparent"}>
                  Data Governance
                </span>
              </h1>

              <p className={`text-sm sm:text-base leading-relaxed font-normal mb-8 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                AxonMetric Global Private Limited operates with enterprise-grade transparency. Review our data storage architecture, sensor permission governance, and account deletion protocols.
              </p>

              {/* Quick Actions Bar */}
              <div className={`flex flex-wrap items-center gap-3 pt-6 border-t text-xs ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Version 2.4 • Updated September 2026</span>
                <span className={`hidden sm:inline ${isDarkMode ? 'text-slate-700' : 'text-gray-300'}`}>•</span>
                <button
                  onClick={() => window.print()}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-semibold transition-colors shadow-sm ${
                    isDarkMode 
                      ? 'border-slate-700 bg-slate-800/90 hover:bg-slate-700 text-slate-200' 
                      : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <Printer className={`w-3.5 h-3.5 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} /> Print Document
                </button>
                <button
                  onClick={handleCopyLink}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-semibold transition-colors shadow-sm ${
                    isDarkMode 
                      ? 'border-slate-700 bg-slate-800/90 hover:bg-slate-700 text-slate-200' 
                      : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className={`w-3.5 h-3.5 ${isDarkMode ? 'text-sky-400' : 'text-[#1C4D88]'}`} />}
                  {copiedLink ? 'Link Copied!' : 'Share'}
                </button>
                <Link
                  to="/account-deletion"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-colors shadow-sm border ${
                    isDarkMode 
                      ? 'bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20' 
                      : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                  }`}
                >
                  Account Deletion Portal <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  to="/terms-and-conditions"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold transition-colors shadow-sm border ${
                    isDarkMode 
                      ? 'bg-sky-500/10 border-sky-500/30 text-sky-400 hover:bg-sky-500/20' 
                      : 'bg-blue-50 border-blue-200 text-[#1C4D88] hover:bg-blue-100'
                  }`}
                >
                  Terms & Conditions <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 4-Item KPI Metrics Row */}
        <section className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((kpi, idx) => {
              const Icon = kpi.icon;
              return (
                <div
                  key={idx}
                  className={`p-5 rounded-2xl border transition-all flex flex-col justify-between ${
                    isDarkMode 
                      ? 'bg-[#111827] border-slate-800 shadow-xl' 
                      : 'bg-white border-gray-200 shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-bold uppercase tracking-wide ${
                      isDarkMode ? 'text-slate-400' : 'text-gray-500'
                    }`}>{kpi.label}</span>
                    <div className={`p-2 rounded-xl ${kpi.bg}`}>
                      <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                    </div>
                  </div>
                  <div>
                    <div className={`text-2xl font-extrabold tracking-tight ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{kpi.value}</div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{kpi.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Search Toolbar & Horizontal Category Pills */}
        <section className="max-w-7xl mx-auto px-6 mb-8">
          <div className={`border rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 transition-colors ${
            isDarkMode 
              ? 'bg-[#111827] border-slate-800 shadow-xl' 
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            {/* Search Input */}
            <div className="relative w-full md:w-96">
              <Search className={`w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 ${
                isDarkMode ? 'text-slate-400' : 'text-gray-400'
              }`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search topics (e.g. GPS, Camera, Sentry, Retention)..."
                className={`w-full pl-10 pr-4 py-2.5 text-xs rounded-xl focus:outline-none focus:ring-2 transition-all ${
                  isDarkMode 
                    ? 'bg-slate-800/80 border border-slate-700 text-white placeholder-slate-400 focus:border-sky-500 focus:ring-sky-500/20' 
                    : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-[#1C4D88]/20 focus:border-[#1C4D88]'
                }`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                    isDarkMode ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Clean Category Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
              {[
                { id: 'all', label: 'All Topics' },
                { id: 'general', label: 'Overview' },
                { id: 'data', label: 'Data Types' },
                { id: 'mobile', label: 'Mobile Sensors' },
                { id: 'security', label: 'Storage & Encryption' },
                { id: 'rights', label: 'User Rights' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? isDarkMode ? 'bg-sky-600 text-white shadow-md shadow-sky-900/30' : 'bg-[#1C4D88] text-white shadow-sm'
                      : isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Policy Content Sections */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="space-y-8">
            {filteredSections.length === 0 ? (
              <div className={`rounded-2xl border p-12 text-center space-y-3 shadow-sm ${
                isDarkMode ? 'bg-[#111827] border-slate-800' : 'bg-white border-gray-200'
              }`}>
                <AlertCircle className={`w-10 h-10 mx-auto ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                <h4 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No matching sections found</h4>
                <p className={`text-xs max-w-sm mx-auto ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  Try refining your search keyword or click "All Topics" to see all disclosures.
                </p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveTab('all'); }}
                  className={`px-4 py-2 text-xs font-bold rounded-xl ${
                    isDarkMode ? 'bg-sky-600 text-white hover:bg-sky-500' : 'bg-[#1C4D88] text-white'
                  }`}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              filteredSections.map((sec) => {
                const SecIcon = sec.icon;
                return (
                  <div
                    key={sec.id}
                    id={sec.id}
                    className={`border rounded-2xl p-6 sm:p-8 transition-all scroll-mt-24 ${
                      isDarkMode 
                        ? 'bg-[#111827] border-slate-800 text-slate-200 hover:border-sky-500/30 shadow-xl' 
                        : 'bg-white border-gray-200 text-gray-900 hover:border-[#1C4D88]/30 shadow-sm'
                    }`}
                  >
                    <div className={`flex items-center gap-3 mb-5 border-b pb-4 ${
                      isDarkMode ? 'border-slate-800' : 'border-gray-100'
                    }`}>
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${
                        isDarkMode ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20' : 'bg-blue-50 text-[#1C4D88]'
                      }`}>
                        <SecIcon className="w-5 h-5" />
                      </div>
                      <h3 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{sec.title}</h3>
                    </div>
                    {sec.content}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default PrivacyPolicyPage;
